import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { formatDate } from '../../lib/utils';
import { Plus, Trash, Download, CheckCircle } from '@phosphor-icons/react';

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export const CreateInvoiceDialog = ({
  open, onOpenChange, agencies, formData, setFormData,
  onAddLineItem, onUpdateLineItem, onRemoveLineItem, calculateTotal, onCreate,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
      <DialogHeader>
        <DialogTitle>Create New Invoice</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label>Agency *</Label>
          <Select value={formData.agency_id} onValueChange={(v) => setFormData({ ...formData, agency_id: v })}>
            <SelectTrigger><SelectValue placeholder="Select agency" /></SelectTrigger>
            <SelectContent>
              {(agencies || []).filter((a) => a.status === 'active').map((agency) => (
                <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Period Start *</Label>
            <Input type="date" value={formData.period_start} onChange={(e) => setFormData({ ...formData, period_start: e.target.value })} />
          </div>
          <div>
            <Label>Period End *</Label>
            <Input type="date" value={formData.period_end} onChange={(e) => setFormData({ ...formData, period_end: e.target.value })} />
          </div>
          <div>
            <Label>Due Date *</Label>
            <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Line Items</Label>
            <Button variant="outline" size="sm" onClick={onAddLineItem}>
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>

          <div className="space-y-2">
            {formData.line_items.map((item, index) => (
              <div key={`line-${index}-${item.description}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Input placeholder="Description" value={item.description} onChange={(e) => onUpdateLineItem(index, 'description', e.target.value)} />
                </div>
                <div className="col-span-2">
                  <Input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={(e) => onUpdateLineItem(index, 'quantity', parseInt(e.target.value, 10) || 1)} />
                </div>
                <div className="col-span-2">
                  <Input type="number" placeholder="Unit Price" min="0" step="0.01" value={item.unit_price} onChange={(e) => onUpdateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-2">
                  <Input readOnly value={formatCurrency(item.total)} className="bg-slate-50" />
                </div>
                <div className="col-span-1">
                  <Button variant="ghost" size="sm" onClick={() => onRemoveLineItem(index)} disabled={formData.line_items.length <= 1} className="text-red-600">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4 pt-4 border-t">
            <div className="text-right">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold">{formatCurrency(calculateTotal())}</p>
            </div>
          </div>
        </div>

        <div>
          <Label>Notes</Label>
          <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes for this invoice" />
        </div>
      </div>

      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onCreate}>Create Invoice</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ViewInvoiceDialog = ({ open, onOpenChange, invoice, statusConfig, paymentMethods, onDownload }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl" data-testid="view-invoice-dialog">
      <DialogHeader><DialogTitle>Invoice Details</DialogTitle></DialogHeader>
      {invoice && (
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-slate-500">Invoice Number</p>
              <p className="font-mono font-bold">{invoice.id}</p>
            </div>
            <Badge className={statusConfig[invoice.status]?.color}>
              {statusConfig[invoice.status]?.label}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">Agency</p>
              <p className="font-medium">{invoice.agency_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Billing Period</p>
              <p>{formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Due Date</p>
              <p>{formatDate(invoice.due_date)}</p>
            </div>
            {invoice.paid_date && (
              <div>
                <p className="text-sm text-slate-500">Paid Date</p>
                <p className="text-green-600">{formatDate(invoice.paid_date)}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm text-slate-500 mb-2">Line Items</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.line_items?.map((item, index) => (
                  <TableRow key={item.description || `detail-${index}`}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-medium">Total</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(invoice.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {invoice.payment_method && (
            <div>
              <p className="text-sm text-slate-500">Payment Method</p>
              <p>{paymentMethods.find((m) => m.value === invoice.payment_method)?.label}</p>
              {invoice.transaction_id && (
                <p className="text-xs text-slate-400 font-mono">Ref: {invoice.transaction_id}</p>
              )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => onDownload(invoice)} data-testid="view-download-pdf">
              <Download size={16} className="mr-2" /> Download PDF
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const RecordPaymentDialog = ({
  open, onOpenChange, invoice, paymentData, setPaymentData, paymentMethods, onRecord,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="record-payment-dialog">
      <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
      {invoice && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex justify-between">
              <div>
                <p className="text-sm text-slate-500">Invoice</p>
                <p className="font-mono">{invoice.id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Amount</p>
                <p className="text-xl font-bold">{formatCurrency(invoice.total)}</p>
              </div>
            </div>
          </div>

          <div>
            <Label>Payment Method *</Label>
            <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    <div className="flex items-center gap-2">
                      <method.icon size={16} />{method.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Transaction ID / Reference</Label>
            <Input value={paymentData.transaction_id} onChange={(e) => setPaymentData({ ...paymentData, transaction_id: e.target.value })} placeholder="Optional reference number" />
          </div>

          <div>
            <Label>Notes</Label>
            <Input value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} placeholder="Optional payment notes" />
          </div>
        </div>
      )}
      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onRecord} className="bg-green-600 hover:bg-green-700">
          <CheckCircle size={16} className="mr-2" /> Record Payment
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
